/**
 * @fileoverview Generic dropdown menu component
 * @description Renders a dropdown menu button (text or avatar) with a list of items
 * Handles navigation for regular items and triggers logout action for 'Log Out' item
 * @active Client component [CONFIRMED] Used in SubHeader
 *
 * @see {@link src/components/ui/header/SubHeader.tsx} - Parent component using this dropdown
 * @see {@link src/services/auth.service.ts} - Logout mutation hook (`useUserSignOutMutation`)
 * @see {@link src/assets/data/menuList/menuList.ts} - Defines menu item structures
 */

"use client";

import {
  Avatar,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation"; // Import useRouter
import { useSelector } from "react-redux";

import ChevronDownSvg from "@/assets/svg/chevron-down.svg";
import { useActions } from "@/hooks/useActions";
import { useUserSignOutMutation } from "@/services/auth.service";
import contentSelectors from "@/store/content/content.selectors";
import { h_700_14_16, h_700_16_100_32, h_700_16_16 } from "@/styles/fontStyles";

/**
 * Renders a dropdown menu with navigation links and logout functionality
 *
 * @component Dropdown
 * @param {object} props - Component props
 * @param {string} [props.textColor='white'] - Color for text elements
 * @param {object} [props.customStyles] - Custom styles for the MenuButton
 * @param {string} props.titleButton - Text for the dropdown button (if not profile)
 * @param {Array<object>} props.menuList - Array of menu item objects ({ id, title, route })
 * @param {string} [props.menuListBgColor='white'] - Background color for the menu list
 * @param {boolean} [props.isSubMenu=false] - Style flag for submenus
 * @param {string | boolean} [props.src=false] - Avatar source URL (if profile dropdown)
 * @param {string | boolean} [props.userName=false] - User name to display in profile dropdown
 * @param {string} [props.left='0px'] - CSS 'left' value for MenuList positioning
 * @param {string} [props.minW] - Minimum width for MenuList
 * @param {boolean} props.isProfile - Flag indicating if this is the profile dropdown
 * @returns {JSX.Element} Rendered dropdown menu component
 */
export const Dropdown = ({
  textColor = "white",
  customStyles,
  titleButton,
  menuList,
  menuListBgColor = "white",
  isSubMenu = false,
  src = false,
  userName = false,
  left = "0px",
  minW,
  isProfile,
}) => {
  const pathName = usePathname();
  const router = useRouter(); // Initialize router

  const { setCurrentNavLink } = useActions(); // Removed userLogOut from useActions
  const currentNavLink = useSelector(contentSelectors.getCurrentNavLink());

  const { isOpen, onOpen, onClose } = useDisclosure(); // Chakra hook for menu state
  const [userSignOut, { isLoading: isSigningOut }] = useUserSignOutMutation(); // RTK Query logout hook

  /**
   * Handles the logout process
   * Calls the `userSignOut` mutation and redirects to home on success
   * @function logOutHandler
   */
  const logOutHandler = async () => {
    try {
      await userSignOut().unwrap(); // Trigger logout via RTK Query
      // Redirect to home page after successful sign out
      router.push("/");
      // No need for legacy userLogOut() or signOut()
    } catch (error) {
      console.error("Logout failed:", error);
      // Optionally show an error toast to the user
    }
  };

  const checkIsUnderLine = (titleButton) => {
    return (
      titleButton === "Research" &&
      ["companies", "funds", "people"].includes(pathName.split("/")[1])
    );
  };

  return (
    <Menu onClose={onClose} isOpen={isOpen} onOpen={onOpen}>
      <MenuButton
        as={Button}
        bg={"none"}
        borderRadius={"0px"}
        color={textColor}
        _hover={{ bg: "none" }}
        _focus={{ bg: "none" }}
        _active={{ bg: "none" }}
        px={"0px"}
        h={"full"}
        onClick={() => {
          setCurrentNavLink(titleButton);
        }}
        {...(!isSubMenu && !src && { borderBottom: "2px" })}
        borderColor={
          !isSubMenu &&
          !src &&
          currentNavLink === titleButton &&
          isOpen &&
          !isProfile
            ? "red"
            : "transparent"
        }
        {...customStyles}
      >
        {!isProfile && (
          <Flex align={"center"} gap={"4px"}>
            <Text
              {...h_700_16_16}
              {...(isSubMenu && { borderBottom: "2px" })}
              borderColor={
                checkIsUnderLine(titleButton)
                  ? "red"
                  : isSubMenu && currentNavLink === titleButton && isOpen
                    ? "red"
                    : "transparent"
              }
            >
              {titleButton}
            </Text>
            <Box
              transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
              transition={"transform 0.3s ease"}
              mb={"4px"}
            >
              <ChevronDownSvg stroke={textColor} />
            </Box>
          </Flex>
        )}
        {isProfile && (
          <Avatar
            size="sm"
            w={"36px"}
            h={"36px"}
            bg={"borderGray"}
            borderWidth={"2px"}
            borderColor={"black"}
            // Pass undefined if src is not a string to satisfy Avatar prop type
            src={typeof src === "string" ? src : undefined}
          />
        )}
      </MenuButton>
      <MenuList
        bg={menuListBgColor}
        border={"none"}
        position="absolute"
        top="-8px"
        left={left}
        borderTopLeftRadius={"0px"}
        borderTopRightRadius={"0px"}
        zIndex={1100}
        minW={minW || "fit-content"}
        p={"12px"}
      >
        {userName && (
          <Box py={"10px"} mt={"8px"}>
            <Text {...h_700_16_100_32} color={"grey"}>
              {userName}
            </Text>
          </Box>
        )}
        {menuList?.map((item, index) => (
          <MenuItem
            key={`${item?.title}-${index}`}
            bg={menuListBgColor}
            color={textColor}
            _hover={{ bg: "none" }}
            p={"0px"}
            // Disable the item while signing out
            isDisabled={item?.title === "Log Out" && isSigningOut}
          >
            {item?.title === "Log Out" ? (
              // Render as Button/Text for Log Out to handle click directly
              <Box
                as="button" // Use button semantics
                onClick={logOutHandler}
                p={"8px 12px 8px 0px"}
                textAlign="left" // Ensure text aligns left like other items
                w="100%" // Make it take full width of MenuItem
                disabled={isSigningOut} // Disable button during sign out
              >
                <Text
                  maxW={"fit-content"}
                  {...h_700_14_16}
                  borderBottom={"2px"}
                  borderColor={"transparent"}
                  transition={"border-color 0.3s ease"}
                  color={isSigningOut ? "gray.400" : "inherit"} // Dim text when disabled
                  _hover={{
                    borderColor: isSigningOut ? "transparent" : "red", // No hover effect when disabled
                    color: isSigningOut ? "gray.400" : "red",
                  }}
                >
                  {isSigningOut ? "Logging out..." : item?.title}
                </Text>
              </Box>
            ) : (
              // Render other items as Links using the modern Link component
              <Link href={item?.route}>
                {/* Removed passHref and legacyBehavior */}
                {/* Removed as="a" from Box, Link handles the anchor tag */}
                <Box p={"8px 12px 8px 0px"} display="block">
                  <Text
                    maxW={"fit-content"}
                    {...h_700_14_16}
                    borderBottom={"2px"}
                    borderColor={"transparent"}
                    transition={"border-color 0.3s ease"}
                    _hover={{
                      borderColor: "red",
                    }}
                  >
                    {item?.title}
                  </Text>
                </Box>
              </Link>
            )}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
